import { useBoolean } from '@blockworks/platform/client';
import { FlexBox } from '@blockworks/ui';
import { Bit } from '@blockworks/ui/factory';
import { cn } from '@blockworks/ui/system';

import { MarkdownRenderer } from '../markdown';

import { MarkdownEditor } from './markdown-editor';
import { MarkdownEditorPreviewTabs } from './markdown-editor-with-preview-tabs';

type MarkdownEditorWithPreviewProps = {
    markdown: string;
    onChange: (value: string) => void;
    placeholder?: string;
    editorProps?: { className?: string };
    previewProps?: { className?: string; placeholder?: string };
    forceTabs?: boolean;
    className?: string;
};

/**
 * A markdown editor with preview. The preview is hidden behind tabs at `< md` breakpoint.
 * Use `forceTabs` to forcefully hide the preview and show the tabs on all screen sizes.
 **/
const MarkdownEditorWithPreview = ({
    markdown,
    onChange,
    placeholder,
    editorProps = {},
    previewProps = {},
    forceTabs = false,
    className,
}: MarkdownEditorWithPreviewProps) => {
    const isPreviewing = useBoolean(false);
    const isEditing = !isPreviewing.value;
    return (
        <FlexBox col w="full" h="full" maxH="full" className={cn(className)}>
            <MarkdownEditorPreviewTabs
                isPreviewing={isPreviewing.value}
                setIsPreviewing={isPreviewing.setTrue}
                setIsEditing={isPreviewing.setFalse}
                display={{ lg: forceTabs ? 'flex' : 'hidden' }}
            />
            <FlexBox w="full" h="full">
                <Bit.div
                    w="full"
                    h="full"
                    className={cn({
                        hidden: isPreviewing.value,
                        'lg:w-[50%] lg:min-w-[50%] lg:flex': !forceTabs,
                        'lg:hidden': isPreviewing.value && forceTabs,
                    })}
                >
                    <MarkdownEditor
                        onChange={onChange}
                        markdown={markdown}
                        editorProps={editorProps}
                        placeholder={placeholder}
                    />
                </Bit.div>
                <MarkdownRenderer.Wrapper
                    withScroller
                    border={1}
                    w="full"
                    className={cn(
                        {
                            hidden: isEditing,
                            'lg:hidden': isEditing && forceTabs,
                            'lg:w-[50%] lg:flex lg:bg-surface-deselected': !forceTabs,
                        },
                        previewProps.className,
                    )}
                >
                    <MarkdownRenderer.Memoized markdown={markdown} showNoContentPlaceholder />
                </MarkdownRenderer.Wrapper>
            </FlexBox>
        </FlexBox>
    );
};

export { MarkdownEditorWithPreview };
